import { Box, Button, Container, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { RevealOverlay } from '@/components/background/RevealOverlay';
import { setting, usePublicContent } from './use-public-content';

/**
 * Above the fold. The panel motif is a CSS gradient grid rather than a photo —
 * it keeps the monochrome palette intact and costs no image bytes.
 */
export function Hero() {
  const { t } = useTranslation();
  const { settings } = usePublicContent();
  const percent = setting(settings, 'company.savingsPercent', '40');

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderBottom: 1,
        borderColor: 'divider',
        py: { xs: 8, md: 14 },
      }}
    >
      <HeroPhoto />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 4, sm: 5, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 7 }}>
            <Stack spacing={3} alignItems="flex-start">
              <Typography variant="overline" color="text.secondary">
                {t('hero.eyebrow')}
              </Typography>
              <Typography variant="h1">{t('hero.title')}</Typography>
              <Typography variant="subtitle1" component="p" color="text.secondary" sx={{ maxWidth: 620 }}>
                {t('hero.subtitle', { percent })}
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}
              >
                <Button
                  href="#kontakt"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                >
                  {t('hero.ctaPrimary')}
                </Button>
                <Button
                  href="#leistungen"
                  variant="outlined"
                  size="large"
                  color="inherit"
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 5 }}>
            <SavingsBadge percent={percent} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function SavingsBadge({ percent }: { percent: string }) {
  const { t } = useTranslation();
  const surface = {
    color: 'secondary.contrastText',
    bgcolor: 'secondary.main',
    boxShadow: (th: Theme) =>
      th.palette.mode === 'dark'
        ? '0 20px 60px rgba(245,166,35,.18)'
        : '0 18px 50px rgba(14,42,92,.22)',
  } as const;

  return (
    <>
      {/* Phones: a compact bar. A 200px+ circle costs a third of the screen
          and pushes the form far below the fold. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ ...surface, display: { xs: 'flex', sm: 'none' }, borderRadius: 3, px: 2.5, py: 1.75 }}
      >
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, lineHeight: 1 }}>
          {percent}%
        </Typography>
        <Stack spacing={0.25}>
          <Typography variant="overline" sx={{ lineHeight: 1.3 }}>
            {t('hero.badge', { percent })}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.3 }}>
            {t('hero.badgeNote')}
          </Typography>
        </Stack>
      </Stack>

      {/* Tablet and up: the circle from the flyer. */}
      <Box
        sx={{
          ...surface,
          display: { xs: 'none', sm: 'grid' },
          aspectRatio: '1 / 1',
          width: '100%',
          maxWidth: { sm: 260, md: 340 },
          mx: 'auto',
          borderRadius: '50%',
          placeItems: 'center',
          textAlign: 'center',
          p: 4,
        }}
      >
        <Stack spacing={0.5}>
          <Typography sx={{ fontSize: 'clamp(2.75rem, 7vw, 4.75rem)', fontWeight: 800, lineHeight: 1 }}>
            {percent}%
          </Typography>
          <Typography variant="overline" sx={{ opacity: 0.85 }}>
            {t('hero.badge', { percent })}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75, maxWidth: 220, mx: 'auto' }}>
            {t('hero.badgeNote')}
          </Typography>
        </Stack>
      </Box>
    </>
  );
}

/**
 * Rooftop photograph behind the hero. The navy scrim is not decoration: it
 * holds the headline and body text above AA contrast over a bright sky, and
 * the gradient keeps the right side readable where the badge sits.
 */
function HeroPhoto() {
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box
        component="img"
        src="/images/roof-sunset.webp"
        srcSet="/images/roof-sunset-sm.webp 900w, /images/roof-sunset.webp 1920w"
        sizes="100vw"
        alt=""
        loading="eager"
        fetchPriority="high"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center 60%',
          opacity: (t) => (t.palette.mode === 'dark' ? 0.62 : 0.55),
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(100deg, rgba(7,18,35,.95) 0%, rgba(7,18,35,.72) 42%, rgba(7,18,35,.2) 100%)'
              : 'linear-gradient(100deg, rgba(244,247,251,.96) 0%, rgba(244,247,251,.78) 42%, rgba(244,247,251,.3) 100%)',
        }}
      />
      <RevealOverlayForMode />
    </Box>
  );
}

/** Strokes have to invert with the mode: white over the navy scrim, navy over the pale one. */
function RevealOverlayForMode() {
  const theme = useTheme();
  return (
    <RevealOverlay
      radius={340}
      color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(14,42,92,0.4)'}
    />
  );
}
